import gql from 'graphql-tag';
import apolloClient from '../apollo';

const defaultParty = () => {
  return {
    _id: '_new',
    number: '_new',
    kind: 'PERSON',
    eligibleFor: [],
    salutation: null,
    company: '',
    firstname: '',
    lastname: '',
    street: '',
    streetNumber: '',
    streetNumberSuffix: '',
    zip: '',
    city: '',
    cityPart: '',
    phone: '',
    phone2: '',
    phone3: '',
    mobile: '',
    mobile2: '',
    email: '',
    email2: '',
    birthdayAt: null,
    taxNumber: '',
    vatId: '',
    bank: '',
    iban: '',
    bic: '',
    accountHolder: '',
    notes: '',
    attachments: [],
    createdBy: null,
  };
};

const getDefaultState = () => ({
  parties: [],
  partyCount: 0,
});

const state = getDefaultState();

const PARTY_LIST_REQUEST_FIELDS = gql`
  fragment partyListFields on Party {
    _id
    number
    kind
    eligibleFor
    company
    firstname
    lastname
    email
    city
    zip
    createdBy {
      _id
      firstname
      lastname
      email
    }
    updatedBy {
      _id
      firstname
      lastname
      email
    }
    createdAt
    updatedAt
  }
`;

const PARTY_REQUEST_FIELDS = gql`
  fragment partyFields on Party {
    _id
    number
    kind
    eligibleFor
    salutation
    company
    firstname
    lastname
    street
    streetNumber
    streetNumberSuffix
    zip
    city
    cityPart
    phone
    phone2
    phone3
    mobile
    mobile2
    email
    email2
    birthdayAt
    taxNumber
    vatId
    bank
    iban
    bic
    accountHolder
    notes
    attachments {
      id
      path
      filename
      displayFilename
      size
      mimetype
      attachmentType
    }
    attachmentTypes
    createdBy {
      _id
      firstname
      lastname
      email
    }
    updatedBy {
      _id
      firstname
      lastname
      email
    }
    createdAt
    updatedAt
  }
`;

const createPayload = (party) => {
  const payload = {
    kind: party.kind,
    eligibleFor: party.eligibleFor && party.eligibleFor.length ? party.eligibleFor : undefined,
    salutation: party.salutation || undefined,
    company: party.company || undefined,
    firstname: party.firstname || undefined,
    lastname: party.lastname || undefined,
    street: party.street || undefined,
    streetNumber: party.streetNumber || undefined,
    streetNumberSuffix: party.streetNumberSuffix || undefined,
    zip: party.zip || undefined,
    city: party.city || undefined,
    cityPart: party.cityPart || undefined,
    phone: party.phone || undefined,
    phone2: party.phone2 || undefined,
    phone3: party.phone3 || undefined,
    mobile: party.mobile || undefined,
    mobile2: party.mobile2 || undefined,
    email: party.email || undefined,
    email2: party.email2 || undefined,
    birthdayAt: party.birthdayAt || undefined,
    taxNumber: party.taxNumber || undefined,
    vatId: party.vatId || undefined,
    bank: party.bank || undefined,
    iban: party.iban || undefined,
    bic: party.bic || undefined,
    accountHolder: party.accountHolder || undefined,
    notes: party.notes || undefined,
  };
  return Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== undefined));
};

const actions = {
  async fetchPartiesPaginated({ commit }, { pagination, sorting, filters }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query partiesPaginated(
            $pagination: PaginationInput
            $sorting: SortingInput
            $filters: PartyFilterInput
          ) {
            partiesPaginated(pagination: $pagination, sorting: $sorting, filters: $filters) {
              parties {
                ...partyListFields
              }
              partyCount
            }
          }
          ${PARTY_LIST_REQUEST_FIELDS}
        `,
        variables: { pagination, sorting, filters },
        fetchPolicy: 'network-only',
      });
      commit('SET_PARTIES', response.data.partiesPaginated.parties);
      commit('SET_PARTY_COUNT', response.data.partiesPaginated.partyCount);
    } catch (err) {
      console.error(err);
      throw err;
    }
  },

  async fetchPartyByNumber({ commit }, partyNumber) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query partyByNumber($partyNumber: Int!) {
            partyByNumber(partyNumber: $partyNumber) {
              ...partyFields
            }
          }
          ${PARTY_REQUEST_FIELDS}
        `,
        variables: { partyNumber: parseInt(partyNumber, 10) },
      });
      commit('ADD_OR_UPDATE_PARTY', response.data.partyByNumber);
    } catch (err) {
      throw new Error(err);
    }
  },

  async createParty({ commit, dispatch }, party) {
    const payload = createPayload(party);
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation createParty($payload: PartyInput!) {
            createParty(partyInput: $payload) {
              ...partyFields
            }
          }
          ${PARTY_REQUEST_FIELDS}
        `,
        variables: { payload },
      });
      dispatch('message', {
        message: 'Geschäftspartner gespeichert!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_PARTY', '_new');
      commit('ADD_OR_UPDATE_PARTY', response.data.createParty);
      return response.data.createParty.number;
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim Erstellen! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw err;
    }
  },

  async updateParty({ commit, dispatch }, party) {
    const payload = createPayload(party);
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation updateParty($partyNumber: Int!, $payload: PartyInput!) {
            updateParty(partyNumber: $partyNumber, partyInput: $payload) {
              ...partyFields
            }
          }
          ${PARTY_REQUEST_FIELDS}
        `,
        variables: {
          partyNumber: party.number,
          payload,
        },
      });
      dispatch('message', {
        message: 'Geschäftspartner aktualisiert!',
        type: 'success',
        lifetime: 2,
      });
      commit('ADD_OR_UPDATE_PARTY', response.data.updateParty);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim Bearbeiten! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw err;
    }
  },

  async deleteParty({ commit, dispatch }, partyNumber) {
    try {
      await apolloClient.mutate({
        mutation: gql`
          mutation deleteParty($partyNumber: Int!) {
            deleteParty(partyNumber: $partyNumber)
          }
        `,
        variables: { partyNumber },
      });
      dispatch('message', {
        message: 'Geschäftspartner gelöscht!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_PARTY', partyNumber);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim Löschen!',
        type: 'danger',
        lifetime: 1,
      });
      throw err;
    }
  },

  async uploadPartyAttachment({ commit, dispatch }, payload) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation uploadPartyAttachment($attachmentInput: AttachmentWithTypeInput) {
            uploadPartyAttachment(attachmentInput: $attachmentInput) {
              ...partyFields
            }
          }
          ${PARTY_REQUEST_FIELDS}
        `,
        variables: {
          attachmentInput: {
            referenceId: payload.parentId,
            referenceType: payload.parentType,
            file: payload.file,
          },
        },
      });
      commit('ADD_OR_UPDATE_PARTY', response.data.uploadPartyAttachment);
      dispatch('message', {
        message: 'Anhang hochgeladen!',
        type: 'success',
        lifetime: 2,
      });
      return response.data.uploadPartyAttachment;
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim hochladen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async deletePartyAttachment({ commit, dispatch }, { partyId, attachmentId }) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation deletePartyAttachment($partyId: ID!, $attachmentId: ID!) {
            deletePartyAttachment(partyId: $partyId, attachmentId: $attachmentId) {
              ...partyFields
            }
          }
          ${PARTY_REQUEST_FIELDS}
        `,
        variables: {
          partyId,
          attachmentId,
        },
      });
      commit('ADD_OR_UPDATE_PARTY', response.data.deletePartyAttachment);
      dispatch('message', {
        message: 'Anhang gelöscht!',
        type: 'success',
        lifetime: 2,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim löschen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  initParty({ commit }) {
    commit('ADD_OR_UPDATE_PARTY', defaultParty());
  },

  partyLocalUpdate({ commit }, party) {
    commit('ADD_OR_UPDATE_PARTY', party);
  },

  partyCleanup({ commit }) {
    commit('REMOVE_PARTY', '_new');
  },
};

const mutations = {
  SET_DEFAULT_STATE(state) {
    Object.assign(state, getDefaultState());
  },
  SET_PARTIES(state, parties) {
    state.parties = parties || [];
  },
  ADD_OR_UPDATE_PARTY(state, updatedParty) {
    const existing = state.parties.find(
      (p) => p._id === updatedParty._id || p.number === updatedParty.number
    );
    if (existing) {
      Object.assign(existing, updatedParty);
    } else {
      state.parties.push(updatedParty);
    }
  },
  REMOVE_PARTY(state, identifier) {
    const index = state.parties.findIndex((p) => p._id === identifier || p.number === identifier);
    if (index !== -1) {
      state.parties.splice(index, 1);
    }
  },
  SET_PARTY_COUNT(state, partyCount) {
    state.partyCount = partyCount;
  },
};

const getters = {
  getParties: (state) => state.parties,
  getPartyByNumber: (state) => (partyNumber) => state.parties.find((p) => p.number == partyNumber),
  getPartyCount: (state) => state.partyCount,
};

export default {
  state,
  getters,
  actions,
  mutations,
};
